/**
 * A work timeline with its data: the range (remembered per place, or fixed),
 * the runs for it (useWorkTimeline.ts), and optionally the filter chips and
 * the lanes toggle. The Timeline view and every embedding — a plan, a
 * roadmap item, a task, an agent in Mission Control — are this with a
 * different filter.
 */
import { Funnel } from '@gravity-ui/icons';
import { Button, Popover } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { type ReactNode, useMemo, useState } from 'react';

import type { RunFilter } from '@aioe/shared';
import { useMoneyForModels } from '../../workbench/moneyFormat.ts';
import { commitsCappedNote, commitsCompleteFrom, commitsForFilter, legendMainBranch, readShowCommits, writeShowCommits } from './timelineCommitsModel.ts';
import { useCommitTimeline } from './useCommitTimeline.ts';
import type { CommitOverlayInput } from './useCommitOverlay.tsx';
import { type TimelineScope, useWorkTimeline } from './useWorkTimeline.ts';
import { WorkTimeline, type WorkTimelineProps } from './WorkTimeline.tsx';
import { activeWindow, applyChips, chipOptions, chipsLabel, type LanesBy, NO_CHIPS, type RangePreset, readRange, resolveRange, type StoredRange, type TimelineChips, type TimelineRun, type TimeWindow, writeRange } from './workTimelineModel.ts';

function storage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export interface TimelinePanelProps {
  scope: TimelineScope;
  projectId?: string;
  filter?: RunFilter;
  lanesBy?: LanesBy;
  /** Offer Agent / Repository lanes. */
  lanesToggle?: boolean;
  /** Offer the trigger, outcome, team and roadmap chips. */
  chips?: boolean;
  compact?: boolean;
  /** Inside another view: the figures as smaller cards (WorkTimeline `smallStats`). */
  smallStats?: boolean;
  /** Console: no figures (WorkTimeline `hideStats`). */
  hideStats?: boolean;
  /** Where the range is remembered; with `fixedRange` there is no range control. */
  rangeView?: string;
  defaultPreset?: Exclude<RangePreset, 'custom'>;
  fixedRange?: { from: number; to: number };
  filterLabel?: string;
  emptyText?: string;
  hideOpen?: WorkTimelineProps['hideOpen'];
  maxLanesHeight?: number;
  enabled?: boolean;
  /** Above the timeline, beside the chips: the view's own controls. */
  extra?: ReactNode;
  /** A cut made here rather than by the host (one the filter cannot say, such as "any roadmap item"). */
  match?: (run: TimelineRun) => boolean;
  /** Open zoomed to where the runs are (a plan's own active period, first start to last end); Reset shows the whole range. */
  fitToRuns?: boolean;
  /** Offer the commit overlay (D180) and its "Show commits" toggle; read only while it is shown. */
  commits?: boolean;
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted">{title}</span>
      {children}
    </div>
  );
}

function ChipSet<T extends string>({ label, options, value, onChange }: { label: string; options: ReadonlyArray<{ id: T; label: string; count: number }>; value: T; onChange: (v: T) => void }) {
  if (!options.length) return null;
  return (
    <span className="wtl-chipset" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" className="wtl-chip" aria-pressed={value === o.id} onClick={() => onChange(o.id)} data-wtl-chip={`${label}:${o.id}`}>
          {o.label}
          <span className="wtl-chip__n">{o.count}</span>
        </button>
      ))}
    </span>
  );
}

/**
 * Console: no full screen (the workbench's FullScreenPanel is the desktop's own layer). The commit
 * overlay reads the project's history through its GitHub connection (./useCommitTimeline.ts), and its
 * "Show commits" switch is in the toolbar's More menu.
 */
export function TimelinePanel(props: TimelinePanelProps) {
  const { scope, projectId, filter, lanesToggle, chips: showChips, compact, smallStats, hideStats, rangeView, defaultPreset = '7d', fixedRange, filterLabel, emptyText, hideOpen, maxLanesHeight, enabled, extra, match, fitToRuns, commits: offerCommits } = props;
  // The models' costs in the organisation's currency (../../workbench/moneyFormat.ts).
  useMoneyForModels();
  const [range, setRange] = useState<StoredRange & { from: number; to: number }>(() => {
    const stored = rangeView ? readRange(storage(), rangeView, defaultPreset) : { preset: defaultPreset };
    return { ...stored, ...resolveRange(stored, Date.now()) };
  });
  const from = fixedRange?.from ?? range.from;
  const to = fixedRange?.to ?? range.to;
  const [lanesBy, setLanesBy] = useState<LanesBy>(props.lanesBy ?? 'agent');
  const [chips, setChips] = useState<TimelineChips>(NO_CHIPS);

  const data = useWorkTimeline({ scope, projectId, from, to, filter, enabled });
  // The commit overlay (D180): on unless this viewer turned it off; nothing is read while it is off.
  const [showCommits, setShowCommits] = useState(() => readShowCommits(storage()));
  const commitData = useCommitTimeline({ scope, projectId, from, to, enabled: !!offerCommits && showCommits && enabled !== false });
  const repositoryName = useMemo(() => {
    const names = new Map(data.repositories.map((r) => [r.rootId, r.name] as const));
    for (const r of data.runs) if (!names.has(r.rootId)) names.set(r.rootId, r.repositoryName);
    return (rootId: string) => names.get(rootId) ?? (rootId || 'this repository');
  }, [data.repositories, data.runs]);
  // An embedded timeline (a plan's, a task's, an agent's) shows only the commits that belong to it.
  const ownCommits = useMemo(() => commitsForFilter(commitData.commits, filter, data.runs), [commitData.commits, filter, data.runs]);
  const matched = useMemo(() => (match ? data.runs.filter(match) : data.runs), [data.runs, match]);
  const options = useMemo(() => chipOptions(matched), [matched]);
  const shown = useMemo(() => (showChips ? applyChips(matched, chips) : matched), [matched, chips, showChips]);
  const chipped = showChips && chips !== NO_CHIPS && chipsLabel(chips, options) !== '';
  const now = data.at || from - 1;
  // The fit is worked out once, from the first answer that has runs, so a poll never moves the view under the person.
  const [fit, setFit] = useState<{ for: string; win: TimeWindow | undefined } | undefined>(undefined);
  const fitKey = `${from}|${to}`;
  // Set while rendering, not in an effect: React re-renders straight away, before anything is painted.
  if (fitToRuns && !data.loading && matched.length && fit?.for !== fitKey) setFit({ for: fitKey, win: activeWindow(matched, now, { start: from, end: to }) });

  const onRange = (a: number, b: number, preset: RangePreset) => {
    const stored: StoredRange = preset === 'custom' ? { preset, from: a, to: b } : { preset };
    if (rangeView) writeRange(storage(), rangeView, stored);
    setRange({ ...stored, ...resolveRange(stored, Date.now()) });
  };

  const lanesHeight = maxLanesHeight;
  const overlay = useMemo<CommitOverlayInput | undefined>(
    () =>
      offerCommits && showCommits
        ? {
            commits: ownCommits,
            mainBranch: (rootId) => commitData.mainBranches.get(rootId),
            remoteLabel: (rootId) => commitData.remoteLabels.get(rootId),
            repositoryName,
            legendMain: legendMainBranch([...commitData.mainBranches.values()]),
            commitUrl: (c) => commitData.urls.get(`${c.rootId}\u0000${c.hash}`),
            ...(filter || match || (showChips && chips !== NO_CHIPS) ? { onlyRunLanes: true } : {}),
          }
        : undefined,
    [offerCommits, showCommits, commitData, ownCommits, repositoryName, filter, match, showChips, chips],
  );
  const toggleCommits = () => {
    writeShowCommits(storage(), !showCommits);
    setShowCommits(!showCommits);
  };
  // Too many commits for one answer: say how far back they are all there, and offer that span as the range.
  const commitsFrom = useMemo(() => (commitData.capped ? commitsCompleteFrom(commitData.commits, commitData.cappedRoots) : undefined), [commitData]);
  const commitsSince = commitsFrom === undefined ? commitData.commits.length : commitData.commits.filter((c) => c.at >= commitsFrom).length;

  const active = chipped ? chipsLabel(chips, options) : '';
  const hasOptions = options.triggers.length > 0 || options.outcomes.length > 0 || options.teams.length > 0 || options.withRoadmap > 0;
  const label = filterLabel;

  return (
    <div className="flex min-w-0 flex-col gap-2" data-timeline-panel>
      {(showChips && hasOptions) || extra ? (
        <div className="wtl-chips" data-wtl-chips>
          {showChips && hasOptions ? (
            <Popover>
              <Button size="sm" variant="secondary" data-wtl-filter-open>
                <Funnel className="size-3.5" /> Filter{active ? ` · ${active}` : ''}
              </Button>
              <Popover.Content placement="bottom start" className="max-w-[min(30rem,92vw)]">
                <Popover.Dialog className="flex flex-col gap-3 p-3" aria-label="Filter the runs">
                  {options.triggers.length ? (
                    <FilterGroup title="Set going">
                      <ChipSet label="Set going" options={options.triggers} value={chips.trigger} onChange={(trigger) => setChips((c) => ({ ...c, trigger }))} />
                    </FilterGroup>
                  ) : null}
                  {options.outcomes.length ? (
                    <FilterGroup title="Outcome">
                      <ChipSet label="Outcome" options={options.outcomes} value={chips.outcome} onChange={(outcome) => setChips((c) => ({ ...c, outcome }))} />
                    </FilterGroup>
                  ) : null}
                  {options.teams.length ? (
                    <FilterGroup title="Team">
                      <ChipSet label="Team" options={options.teams} value={chips.team} onChange={(team) => setChips((c) => ({ ...c, team }))} />
                    </FilterGroup>
                  ) : null}
                  {options.withRoadmap ? (
                    <FilterGroup title="Roadmap">
                      <span className="wtl-chipset">
                        <button type="button" className="wtl-chip" aria-pressed={chips.roadmapOnly} onClick={() => setChips((c) => ({ ...c, roadmapOnly: !c.roadmapOnly }))} data-wtl-chip="roadmap">
                          Only with a roadmap item <span className="wtl-chip__n">{options.withRoadmap}</span>
                        </button>
                      </span>
                    </FilterGroup>
                  ) : null}
                </Popover.Dialog>
              </Popover.Content>
            </Popover>
          ) : null}
          {chipped ? (
            <button type="button" className="wtl-chip" aria-pressed="true" onClick={() => setChips(NO_CHIPS)} aria-label={`Clear the filter: ${active}`} data-wtl-chips-clear>
              {active} ×
            </button>
          ) : null}
          {extra}
        </div>
      ) : null}
      <WorkTimeline
        runs={shown}
        totals={chipped || match ? undefined : data.totals}
        from={from}
        to={to}
        now={now}
        lanesBy={lanesBy}
        loading={data.loading}
        {...(data.error ? { error: data.error } : {})}
        onRetry={data.refresh}
        {...(fixedRange ? {} : { onRange, preset: range.preset })}
        {...(label ? { filterLabel: label } : {})}
        compact={compact}
        smallStats={smallStats}
        hideStats={hideStats}
        showRepository={scope === 'project' && lanesBy === 'agent'}
        {...(emptyText ? { emptyText } : {})}
        {...(hideOpen ? { hideOpen } : {})}
        {...(lanesHeight ? { maxLanesHeight: lanesHeight } : {})}
        {...(fit?.for === fitKey && fit.win ? { fitWindow: fit.win } : {})}
        // Console: refresh is in the toolbar's More menu (WorkTimeline `onRefresh`), so the row keeps to three controls.
        {...(!compact ? { onRefresh: data.refresh } : {})}
        {...(overlay ? { commits: overlay } : {})}
        // Offered while off, or while on with commits to show: a project with no GitHub history stays quiet.
        {...(offerCommits && (!showCommits || ownCommits.length > 0) ? { commitsToggle: { on: showCommits, toggle: toggleCommits } } : {})}
        actions={
          lanesToggle ? (
            <Segment aria-label="One lane per" size="sm" selectedKey={lanesBy} onSelectionChange={(k) => setLanesBy(String(k) === 'repository' ? 'repository' : 'agent')} data-wtl-lanes-by={lanesBy}>
              <Segment.Item id="agent">Agent</Segment.Item>
              <Segment.Item id="repository">Repository</Segment.Item>
            </Segment>
          ) : null
        }
      />
      {data.truncated ? <p className="m-0 text-xs text-muted">More runs fall in this range than can be shown at once: the newest are here. Choose a shorter range to see the rest.</p> : null}
      {overlay && commitData.capped ? (
        <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted" data-wtl-commits-capped>
          <span>{commitsCappedNote(commitsSince, commitsFrom)}</span>
          {!fixedRange && commitsFrom !== undefined && commitsFrom > from ? (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onPress={() => onRange(commitsFrom, to, 'custom')} data-wtl-commits-narrow>
              Show only that span
            </Button>
          ) : null}
        </p>
      ) : null}
      {overlay && commitData.error ? (
        <p className="m-0 text-xs text-muted" data-wtl-commits-error>
          Some commits could not be read: {commitData.error}
        </p>
      ) : null}
    </div>
  );
}
